/* ==========================================================================
   DCW Blast Gates — Clean Hierarchy Restore
   Paste into: assets/dcw-blast-gates.js

   Restores:
   - Manual: Connection > Material > Gate Type > Diameter
   - Automatic: Actuation > Connection > Configuration > Material > Voltage > Diameter
   ========================================================================== */

(() => {
  "use strict";

  const HIERARCHY = {
    manual: {
      fields: ["connection", "material", "gate_type", "diameter"],
      labels: {
        connection: "Connection",
        material: "Material",
        gate_type: "Gate Type",
        diameter: "Diameter"
      },
      combos: [
        ["Raw Ends", "Die-cast Aluminum", "Full Gate"],
        ["Raw Ends", "Die-cast Aluminum", "Half Gate"],
        ["Raw Ends", "Die-cast Aluminum", "Self-Cleaning"],
        ["Raw Ends", "Die-cast Aluminum", "Self-Cleaning with 304 SS Blade"],

        ["Raw Ends", "Fabricated Galvanized Steel", "Full Gate"],
        ["Raw Ends", "Fabricated Galvanized Steel", "UHMW Seal"],
        ["Raw Ends", "Fabricated Galvanized Steel", "Oil Mist / Vapor Proof"],

        ["Raw Ends", "304 Stainless Steel", "Full Gate"],
        ["Raw Ends", "304 Stainless Steel", "UHMW Seal"],

        ["Raw Ends", "316 Stainless Steel", "Full Gate"],

        ["Rolled Lip", "Die-cast Aluminum", "Full Gate"],
        ["Rolled Lip", "304 Stainless Steel", "Full Gate"]
      ]
    },

    automatic: {
      fields: ["actuation_type", "connection", "configuration", "material", "voltage", "diameter"],
      labels: {
        actuation_type: "Actuation / Type",
        connection: "Connection",
        configuration: "Configuration",
        material: "Material",
        voltage: "Voltage",
        diameter: "Diameter"
      },
      combos: [
        ["Pneumatic", "Raw Ends", "UHMW Seal", "Fabricated Galvanized Steel"],
        ["Pneumatic", "Raw Ends", "UHMW Seal", "Fabricated 304 Stainless Steel"],

        ["Pneumatic", "Raw Ends", "Single Cylinder", "Die-cast Aluminum with Galvanized Frame"],
        ["Pneumatic", "Raw Ends", "Single Cylinder", "Fabricated Galvanized Steel"],

        ["Pneumatic", "Raw Ends", "Double Cylinder", "Die-cast Aluminum with Galvanized Frame"],
        ["Pneumatic", "Raw Ends", "Double Cylinder", "Fabricated Galvanized Steel"],

        ["Pneumatic", "Rolled Lip", "UHMW Seal", "Fabricated Galvanized Steel"],
        ["Pneumatic", "Rolled Lip", "Single Cylinder", "Die-cast Aluminum with Galvanized Frame"],
        ["Pneumatic", "Rolled Lip", "Double Cylinder", "Die-cast Aluminum with Galvanized Frame"]
      ]
    }
  };

  const SORT_ORDER = {
    actuation_type: ["Pneumatic"],
    connection: ["Raw Ends", "Rolled Lip"],
    configuration: ["UHMW Seal", "Single Cylinder", "Double Cylinder"],
    material: [
      "Die-cast Aluminum",
      "Fabricated Galvanized Steel",
      "304 Stainless Steel",
      "316 Stainless Steel",
      "Die-cast Aluminum with Galvanized Frame",
      "Fabricated 304 Stainless Steel"
    ],
    gate_type: [
      "Full Gate",
      "Half Gate",
      "Self-Cleaning",
      "Self-Cleaning with 304 SS Blade",
      "UHMW Seal",
      "Oil Mist / Vapor Proof"
    ],
    voltage: ["110V", "220V", "24VDC", "24VAC"]
  };

  const IMAGE_MAP = {
    manual: {
      default: "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Full_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Die-cast Aluminum|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Full_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Die-cast Aluminum|Half Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Half_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Die-cast Aluminum|Self-Cleaning":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Self_Cleaning_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Die-cast Aluminum|Self-Cleaning with 304 SS Blade":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Stainless_Steel_Self_Cleaning_Blastgate_Half_3.png?v=1778181570",

      "Raw Ends|Fabricated Galvanized Steel|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Full_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Fabricated Galvanized Steel|UHMW Seal":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Galv_UHMW_Manual_Blastgate_3.png?v=1778181571",

      "Raw Ends|Fabricated Galvanized Steel|Oil Mist / Vapor Proof":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Oil_Mist_Vapor_Proof_Sealed_Blastgate_3.png?v=1778181571",

      "Raw Ends|304 Stainless Steel|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Clamp_Together_Stainless_Steel_Full_Blastgate_3.png?v=1778181571",

      "Raw Ends|304 Stainless Steel|UHMW Seal":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Stainless_Steel_Manual_UHMW_Blastgate_3.png?v=1778181571",

      "Raw Ends|316 Stainless Steel|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Clamp_Together_Stainless_Steel_Full_Blastgate_3.png?v=1778181571",

      "Rolled Lip|Die-cast Aluminum|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Clamp_Together_-_Full_Manual_Blastgate_3.png?v=1778181571",

      "Rolled Lip|304 Stainless Steel|Full Gate":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Clamp_Together_Stainless_Steel_Full_Blastgate_3.png?v=1778181571"
    },

    automatic: {
      default: "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Automatic_Single_Blastgate_3.png?v=1778181570",

      "Pneumatic|Raw Ends|UHMW Seal|Fabricated Galvanized Steel":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/UHMW_Seals_Single_Blastgate_3.png?v=1778181571",

      "Pneumatic|Raw Ends|UHMW Seal|Fabricated 304 Stainless Steel":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Stainless_Steel_Automatic_Blastgate_3.png?v=1778181571",

      "Pneumatic|Raw Ends|Single Cylinder|Die-cast Aluminum with Galvanized Frame":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Automatic_Single_Blastgate_3.png?v=1778181570",

      "Pneumatic|Raw Ends|Single Cylinder|Fabricated Galvanized Steel":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Automatic_Single_Blastgate_3.png?v=1778181570",

      "Pneumatic|Raw Ends|Double Cylinder|Die-cast Aluminum with Galvanized Frame":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Double_Cylinder_Automatic_Blastgate_-_Compact_3.png?v=1778181570",

      "Pneumatic|Raw Ends|Double Cylinder|Fabricated Galvanized Steel":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Double_Cylinder_Automatic_Blastgate_-_Compact_3.png?v=1778181570",

      "Pneumatic|Rolled Lip|UHMW Seal|Fabricated Galvanized Steel":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/UHMW_Seals_Single_Blastgate_3.png?v=1778181571",

      "Pneumatic|Rolled Lip|Single Cylinder|Die-cast Aluminum with Galvanized Frame":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Automatic_Single_Blastgate_3.png?v=1778181570",

      "Pneumatic|Rolled Lip|Double Cylinder|Die-cast Aluminum with Galvanized Frame":
        "https://cdn.shopify.com/s/files/1/0719/9247/5785/files/Double_Cylinder_Automatic_Blastgate_-_Compact_3.png?v=1778181570"
    }
  };

  function clean(value) {
    return String(value || "").replace(/\s+/g, " ").trim();
  }

  function searchable(value) {
    return clean(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  function getSkuNumber(sku) {
    const match = clean(sku).match(/525-(\d{5})/);
    return match ? Number(match[1]) : null;
  }

  function inRange(number, start, end) {
    return typeof number === "number" && number >= start && number <= end;
  }

  function getConnection(value, text, type, skuNumber) {
    const combined = searchable(`${value} ${text}`);

    if (type === "automatic") {
      if (inRange(skuNumber, 195, 260) || inRange(skuNumber, 416, 480)) return "Rolled Lip";
      if (inRange(skuNumber, 262, 328) || inRange(skuNumber, 350, 414) || inRange(skuNumber, 481, 540)) return "Raw Ends";
    }

    if (combined.includes("rolled lip") || combined.includes("clamp together")) return "Rolled Lip";
    if (combined.includes("raw end")) return "Raw Ends";

    return clean(value);
  }

  function getMaterial(value, text, type, skuNumber) {
    const combined = searchable(`${value} ${text}`);

    if (type === "automatic") {
      if (inRange(skuNumber, 350, 414) || inRange(skuNumber, 416, 480) || combined.includes("die cast aluminum with galvanized frame")) {
        return "Die-cast Aluminum with Galvanized Frame";
      }

      if (inRange(skuNumber, 481, 540) || combined.includes("fabricated galvanized")) {
        return "Fabricated Galvanized Steel";
      }

      if (combined.includes("fabricated 304") || combined.includes("304 stainless")) {
        return "Fabricated 304 Stainless Steel";
      }
    }

    if (combined.includes("316 stainless")) return "316 Stainless Steel";
    if (combined.includes("304 stainless")) return "304 Stainless Steel";
    if (combined.includes("fabricated galvanized")) return "Fabricated Galvanized Steel";
    if (combined.includes("die cast") || combined.includes("diecast")) return "Die-cast Aluminum";

    return clean(value);
  }

  function getGateType(value, text) {
    const combined = searchable(`${value} ${text}`);

    if (combined.includes("self cleaning") && (combined.includes("304 ss blade") || combined.includes("stainless blade"))) {
      return "Self-Cleaning with 304 SS Blade";
    }

    if (combined.includes("self cleaning")) return "Self-Cleaning";
    if (combined.includes("half gate") || combined.includes("half blast")) return "Half Gate";
    if (combined.includes("uhmw")) return "UHMW Seal";
    if (combined.includes("oil mist") || combined.includes("vapor proof")) return "Oil Mist / Vapor Proof";
    if (combined.includes("full gate") || combined.includes("full blast")) return "Full Gate";

    return clean(value);
  }

  function getConfiguration(value, text, skuNumber) {
    const combined = searchable(`${value} ${text}`);

    if (inRange(skuNumber, 262, 328) || inRange(skuNumber, 195, 260) || combined.includes("uhmw seal")) return "UHMW Seal";
    if (inRange(skuNumber, 350, 365) || inRange(skuNumber, 416, 431) || inRange(skuNumber, 481, 504) || combined.includes("single cylinder")) return "Single Cylinder";
    if (inRange(skuNumber, 366, 414) || inRange(skuNumber, 432, 480) || inRange(skuNumber, 505, 540) || combined.includes("double cylinder")) return "Double Cylinder";

    return clean(value);
  }

  function getVoltage(value, text, skuNumber) {
    const direct = clean(`${value} ${text}`).toUpperCase().match(/\b(110V|220V|24VDC|24VAC)\b/);
    if (direct) return direct[1];

    const ranges = [
      [262, 277, "110V"], [279, 294, "220V"], [296, 311, "24VDC"], [313, 328, "24VAC"],
      [350, 353, "110V"], [354, 357, "220V"], [358, 361, "24VDC"], [362, 365, "24VAC"],
      [366, 377, "110V"], [378, 389, "220V"], [390, 401, "24VDC"], [403, 414, "24VAC"],
      [195, 209, "110V"], [212, 226, "220V"], [228, 242, "24VDC"], [246, 260, "24VAC"],
      [416, 419, "110V"], [420, 423, "220V"], [424, 427, "24VDC"], [428, 431, "24VAC"],
      [432, 443, "110V"], [444, 455, "220V"], [456, 467, "24VDC"], [469, 480, "24VAC"],
      [481, 486, "110V"], [487, 492, "220V"], [493, 498, "24VDC"], [499, 504, "24VAC"],
      [505, 513, "110V"], [514, 522, "220V"], [523, 531, "24VDC"], [532, 540, "24VAC"]
    ];

    const range = ranges.find(([start, end]) => inRange(skuNumber, start, end));

    return range ? range[2] : clean(value);
  }

  function getDiameter(value, text) {
    const fromValue = clean(value).match(/(\d+(?:\.\d+)?)\s*(?:"|in\b|inch\b|dia\b)?/i);
    if (fromValue) return `${fromValue[1]}"`;

    const fromText = clean(text).match(/(\d+(?:\.\d+)?)\s*(?:"|in\b|inch\b|dia\b)/i);
    return fromText ? `${fromText[1]}"` : clean(value);
  }

  function imageKey(url) {
    if (!url) return "";

    try {
      return decodeURIComponent(new URL(url, window.location.origin).pathname.split("/").pop() || "").toLowerCase();
    } catch (error) {
      return String(url).split("?")[0].split("/").pop().toLowerCase();
    }
  }

  function getComboKey(variant, type) {
    if (type === "automatic") {
      return [
        variant.actuation_type,
        variant.connection,
        variant.configuration,
        variant.material
      ].map(clean).join("|");
    }

    return [
      variant.connection,
      variant.material,
      variant.gate_type
    ].map(clean).join("|");
  }

  function getImageForVariant(variant, type) {
    const map = IMAGE_MAP[type] || {};
    return map[getComboKey(variant, type)] || map.default || variant.image || "";
  }

  function normalizeVariant(raw, type) {
    const combinedText = [
      raw.title,
      raw.sku,
      raw.actuation_type,
      raw.connection,
      raw.configuration,
      raw.material,
      raw.gate_type,
      raw.voltage,
      raw.diameter,
      raw.exact_image_file
    ].map(clean).join(" ");

    const skuNumber = getSkuNumber(raw.sku);

    const variant = {
      ...raw,
      id: raw.id,
      title: clean(raw.title),
      sku: clean(raw.sku),
      available: raw.available !== false,
      price: Number(raw.price || 0),
      image: clean(raw.image),
      actuation_type: type === "automatic" ? "Pneumatic" : "",
      connection: getConnection(raw.connection, combinedText, type, skuNumber),
      material: getMaterial(raw.material, combinedText, type, skuNumber),
      gate_type: type === "manual" ? getGateType(raw.gate_type, combinedText) : "",
      configuration: type === "automatic" ? getConfiguration(raw.configuration, combinedText, skuNumber) : "",
      voltage: type === "automatic" ? getVoltage(raw.voltage, combinedText, skuNumber) : "",
      diameter: getDiameter(raw.diameter, combinedText)
    };

    variant.image = getImageForVariant(variant, type);

    return variant;
  }

  function isApprovedVariant(variant, type) {
    const currentCombo = type === "automatic"
      ? [variant.actuation_type, variant.connection, variant.configuration, variant.material]
      : [variant.connection, variant.material, variant.gate_type];

    return HIERARCHY[type].combos.some((approvedCombo) => {
      return approvedCombo.every((approvedValue, index) => {
        return clean(approvedValue) === clean(currentCombo[index]);
      });
    });
  }

  function sortOptions(field, values) {
    const order = SORT_ORDER[field];

    return [...values].sort((a, b) => {
      if (field === "diameter") {
        const aNumber = Number((a.match(/[\d.]+/) || [9999])[0]);
        const bNumber = Number((b.match(/[\d.]+/) || [9999])[0]);

        return aNumber - bNumber;
      }

      if (order) {
        const aIndex = order.indexOf(a);
        const bIndex = order.indexOf(b);

        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      }

      return a.localeCompare(b, undefined, { numeric: true });
    });
  }

  function formatMoney(cents) {
    const dollars = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD"
      }).format(dollars);
    } catch (error) {
      return `$${dollars.toFixed(2)}`;
    }
  }

  class DCWBlastGateConfigurator {
    constructor(root) {
      this.root = root;
      this.type = clean(root.dataset.productType) === "automatic" ? "automatic" : "manual";
      this.fields = HIERARCHY[this.type].fields;
      this.labels = HIERARCHY[this.type].labels;
      this.state = {};
      this.selects = {};
      this.variants = this.getVariants();

      this.selectContainer = root.querySelector("[data-dcw-bg-selects]");
      this.summary = root.querySelector("[data-dcw-bg-summary]");
      this.sku = root.querySelector("[data-dcw-bg-sku]");
      this.price = root.querySelector("[data-dcw-bg-price]");
      this.mainMedia = root.querySelector("[data-dcw-bg-main-media]");
      this.addToCart = root.querySelector("[data-dcw-bg-atc]");
      this.variantInput = root.querySelector('form[action*="/cart/add"] input[name="id"], input[name="id"]');

      this.init();
    }

    getVariants() {
      const script = this.root.querySelector("[data-dcw-bg-variants]");

      if (!script) return [];

      try {
        const rawVariants = JSON.parse(script.textContent || "[]");
        const normalizedVariants = rawVariants.map((variant) => normalizeVariant(variant, this.type));
        const approvedVariants = normalizedVariants.filter((variant) => isApprovedVariant(variant, this.type));

        return approvedVariants.length ? approvedVariants : normalizedVariants;
      } catch (error) {
        console.warn("DCW Blast Gates: variant data could not be parsed.", error);
        return [];
      }
    }

    init() {
      if (!this.selectContainer || !this.variants.length) return;

      const hierarchyBox = this.root.querySelector(".dcw-bg-hierarchy");

      if (hierarchyBox) {
        hierarchyBox.style.display = "none";
      }

      this.buildSelectors();
      this.resetAll();
      this.renderSelectors();
      this.updatePage();
      this.bindQuantityButtons();
      this.bindThumbnails();
    }

    buildSelectors() {
      this.selectContainer.innerHTML = "";

      this.fields.forEach((field) => {
        const row = document.createElement("div");
        row.className = "dcw-bg-select-row";

        const label = document.createElement("label");
        label.className = "dcw-bg-select-label";
        label.textContent = this.labels[field] || field;

        const select = document.createElement("select");
        select.className = "dcw-bg-select";
        select.dataset.dcwBgField = field;
        select.setAttribute("aria-label", this.labels[field] || field);

        select.addEventListener("change", () => {
          this.state[field] = select.value;
          this.resetAfter(field);
          this.renderSelectors();
          this.updatePage();
        });

        row.append(label, select);
        this.selectContainer.append(row);
        this.selects[field] = select;
      });

      if (!this.root.querySelector(".dcw-bg-reset-button")) {
        const resetButton = document.createElement("button");
        resetButton.type = "button";
        resetButton.className = "dcw-bg-reset-button";
        resetButton.textContent = "Reset selection";

        resetButton.addEventListener("click", () => {
          this.resetAll();
          this.renderSelectors();
          this.updatePage();
        });

        this.selectContainer.insertAdjacentElement("afterend", resetButton);
      }
    }

    resetAll() {
      this.state = {};

      this.fields.forEach((field, index) => {
        const options = this.getOptions(field, index);
        this.state[field] = options[0] || "";
      });
    }

    resetAfter(changedField) {
      const changedIndex = this.fields.indexOf(changedField);

      this.fields.forEach((field, index) => {
        if (index <= changedIndex) return;

        const options = this.getOptions(field, index);
        this.state[field] = options[0] || "";
      });
    }

    getMatchingVariantsBefore(fieldIndex) {
      const previousFields = this.fields.slice(0, fieldIndex);

      return this.variants.filter((variant) => {
        return previousFields.every((field) => {
          const selectedValue = clean(this.state[field]);

          return !selectedValue || clean(variant[field]) === selectedValue;
        });
      });
    }

    getOptions(field, fieldIndex) {
      const values = new Set();

      this.getMatchingVariantsBefore(fieldIndex).forEach((variant) => {
        const value = clean(variant[field]);

        if (value) values.add(value);
      });

      return sortOptions(field, Array.from(values));
    }

    renderSelectors() {
      this.fields.forEach((field, index) => {
        const select = this.selects[field];

        if (!select) return;

        const options = this.getOptions(field, index);

        select.innerHTML = "";

        options.forEach((value) => {
          const option = document.createElement("option");

          option.value = value;
          option.textContent = value;

          select.append(option);
        });

        if (!options.includes(this.state[field])) {
          this.state[field] = options[0] || "";
        }

        if (!options.length) {
          const option = document.createElement("option");

          option.value = "";
          option.textContent = "No valid options";

          select.append(option);
        }

        select.value = this.state[field] || "";
        select.disabled = field === "actuation_type" && options.length <= 1;
      });
    }

    getSelectedVariant() {
      return this.variants.find((variant) => {
        return this.fields.every((field) => clean(variant[field]) === clean(this.state[field]));
      }) || null;
    }

    updatePage() {
      const variant = this.getSelectedVariant();

      if (!variant) {
        if (this.summary) {
          this.summary.innerHTML = "<strong>YOUR SELECTION</strong><br>No matching data-backed configuration found.";
        }

        if (this.addToCart) {
          this.addToCart.disabled = true;
          this.addToCart.textContent = "Unavailable";
        }

        return;
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }

      if (this.sku) {
        this.sku.textContent = variant.sku || "";
      }

      if (this.price) {
        this.price.textContent = formatMoney(variant.price);
      }

      if (this.summary) {
        const selectedText = this.fields
          .map((field) => this.state[field])
          .filter(Boolean)
          .join(" / ");

        this.summary.innerHTML = `<strong>YOUR SELECTION</strong><br>${selectedText} <span>SKU: ${variant.sku || ""}</span>`;
      }

      this.updateImage(variant);

      if (this.addToCart) {
        this.addToCart.disabled = variant.available === false;
        this.addToCart.textContent = variant.available === false ? "Unavailable" : "Add to Cart";
      }
    }

    updateImage(variant) {
      if (!variant || !variant.image || !this.mainMedia) return;

      const mainImage = this.mainMedia.querySelector("img");

      if (!mainImage) return;

      mainImage.src = variant.image;
      mainImage.alt = variant.title || variant.sku || "Selected Blast Gate";

      const activeImageKey = imageKey(variant.image);

      this.root.querySelectorAll("[data-dcw-bg-thumb]").forEach((button) => {
        const image = button.querySelector("img");
        const thumbSource = image ? image.dataset.full || image.src : "";
        const isMatchingImage = imageKey(thumbSource) === activeImageKey;

        button.classList.toggle("dcw-bg-thumb--hidden", !isMatchingImage);
        button.style.display = isMatchingImage ? "" : "none";
        button.setAttribute("aria-current", isMatchingImage ? "true" : "false");
      });
    }

    bindQuantityButtons() {
      this.root.querySelectorAll("[data-dcw-bg-qty-minus]").forEach((button) => {
        button.addEventListener("click", () => {
          const input = button.closest(".dcw-bg-qty")?.querySelector("input");

          if (input) {
            input.value = Math.max(1, Number(input.value || 1) - 1);
          }
        });
      });

      this.root.querySelectorAll("[data-dcw-bg-qty-plus]").forEach((button) => {
        button.addEventListener("click", () => {
          const input = button.closest(".dcw-bg-qty")?.querySelector("input");

          if (input) {
            input.value = Number(input.value || 1) + 1;
          }
        });
      });
    }

    bindThumbnails() {
      this.root.querySelectorAll("[data-dcw-bg-thumb]").forEach((button) => {
        button.addEventListener("click", () => {
          const thumbnailImage = button.querySelector("img");
          const mainImage = this.mainMedia?.querySelector("img");

          if (!thumbnailImage || !mainImage) return;

          mainImage.src = thumbnailImage.dataset.full || thumbnailImage.src;
          mainImage.alt = thumbnailImage.alt || "Blast Gate";

          this.root.querySelectorAll("[data-dcw-bg-thumb]").forEach((otherButton) => {
            otherButton.setAttribute("aria-current", otherButton === button ? "true" : "false");
          });
        });
      });
    }
  }

  function start() {
    document.querySelectorAll("[data-dcw-bg-configurator]").forEach((root) => {
      root.__dcwBlastGateConfigurator = new DCWBlastGateConfigurator(root);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();